import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Col, Form, Input, List, Modal, Popconfirm, Row, Tag } from 'antd';
import { del, get, post, put } from '../../../api/client';

interface CatalogItem {
  id: string;
  name: string;
  createdBy: string | null;
  createdAt: string;
}

type Kind = 'service' | 'category';

/**
 * 与后端 `SettingsService.BUILTIN_ORDER_CATEGORIES` 对应：这两项参与收支方向与对方视角判定，
 * 后端也会拒绝改名/删除，这里只是不给出入口，避免管理员点了才知道不行。
 */
const BUILTIN_CATEGORIES = new Set(['收入订单', '支出订单']);

const TITLES: Record<Kind, string> = {
  service: '系统预设服务类型',
  category: '订单分类',
};

/**
 * 订单目录管理。
 *
 * 两栏的权限口径并不相同，这一点在界面上必须说清楚，否则管理员会以为
 * 「服务类型」也能像客户标签那样发给全体用户：
 * - 服务类型：**系统预设**（created_by 为空）对所有用户可见；用户在业务端自建的部分
 *   只有本人看得到，因此这里刻意不列、也改不动（`GET /service-types` 只返回预设 + 本人）。
 * - 订单分类：**全局**目录，所有用户共用一套，写操作只开给管理员。
 */
export function OrderCatalogPanel() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [addKind, setAddKind] = useState<Kind | null>(null);
  const [editTarget, setEditTarget] = useState<{ kind: Kind; id: string; name: string } | null>(null);

  const servicesQuery = useQuery({
    queryKey: ['order-catalog-service-types'],
    queryFn: () => get<CatalogItem[]>('/service-types'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['order-catalog-categories'],
    queryFn: () => get<CatalogItem[]>('/order-categories'),
  });

  const presetServices = (servicesQuery.data ?? []).filter((item) => !item.createdBy);
  const categories = categoriesQuery.data ?? [];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['order-catalog-service-types'] });
    void qc.invalidateQueries({ queryKey: ['order-catalog-categories'] });
  };

  const endpoints = (kind: Kind) =>
    kind === 'service'
      ? { create: '/admin/catalog/preset-service-types', item: (id: string) => `/service-types/${id}` }
      : { create: '/order-categories', item: (id: string) => `/order-categories/${id}` };

  const createItem = useMutation({
    mutationFn: ({ kind, name }: { kind: Kind; name: string }) => post(endpoints(kind).create, { name }),
    onSuccess: (_data, vars) => {
      message.success(`${TITLES[vars.kind]}已创建`);
      setAddKind(null);
      addForm.resetFields();
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '创建失败'),
  });

  const updateItem = useMutation({
    mutationFn: ({ kind, id, name }: { kind: Kind; id: string; name: string }) =>
      put(endpoints(kind).item(id), { name }),
    onSuccess: (_data, vars) => {
      message.success(`${TITLES[vars.kind]}已更新`);
      setEditTarget(null);
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '修改失败'),
  });

  const deleteItem = useMutation({
    mutationFn: ({ kind, id }: { kind: Kind; id: string }) => del(endpoints(kind).item(id)),
    onSuccess: (_data, vars) => {
      message.success(`${TITLES[vars.kind]}已删除`);
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '删除失败'),
  });

  const submitAdd = async () => {
    const values = await addForm.validateFields();
    if (!addKind) return;
    createItem.mutate({ kind: addKind, name: values.name.trim() });
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    const values = await editForm.validateFields();
    updateItem.mutate({ ...editTarget, name: values.name.trim() });
  };

  const renderItem = (kind: Kind, item: CatalogItem) => {
    const builtin = kind === 'category' && BUILTIN_CATEGORIES.has(item.name);
    return (
      <List.Item
        actions={
          builtin
            ? undefined
            : [
                <Button
                  key="edit"
                  size="small"
                  type="link"
                  onClick={() => {
                    setEditTarget({ kind, id: item.id, name: item.name });
                    editForm.setFieldsValue({ name: item.name });
                  }}
                >
                  编辑
                </Button>,
                <Popconfirm
                  key="delete"
                  title={`确定删除「${item.name}」？`}
                  description="删除后各端不再展示该项，已存在的订单不受影响"
                  onConfirm={() => deleteItem.mutate({ kind, id: item.id })}
                >
                  <Button size="small" type="link" danger>
                    删除
                  </Button>
                </Popconfirm>,
              ]
        }
      >
        <span>
          {item.name}
          {builtin && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              系统内置
            </Tag>
          )}
        </span>
      </List.Item>
    );
  };

  const listProps = (kind: Kind, items: CatalogItem[], loading: boolean, emptyText: string) => ({
    size: 'small' as const,
    loading,
    dataSource: items,
    locale: { emptyText },
    renderItem: (item: CatalogItem) => renderItem(kind, item),
  });

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="订单分类与服务类型的区别"
        description="订单分类只表达收支方向，是全平台共用的一套，仅管理员可增删；名称含「支出」二字即按支出记账，「收入订单」「支出订单」为系统内置，参与收支方向与对方视角判定，不可改名或删除。业务场景（如直播、设备租赁、场地布置）请放到服务类型里，用户可各自自定义。"
      />
      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <Card
            type="inner"
            title="系统预设服务类型"
            extra={
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => {
                  setAddKind('service');
                  addForm.resetFields();
                }}
              >
                新增预设
              </Button>
            }
          >
            <List
              {...listProps(
                'service',
                presetServices,
                servicesQuery.isLoading,
                '暂无系统预设服务类型',
              )}
            />
            <div style={{ marginTop: 8, color: 'rgba(0,0,0,.45)', fontSize: 12 }}>
              用户在业务端自建的服务类型仅本人可见，不在此列，管理员也无法修改。
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            type="inner"
            title="订单分类（全平台）"
            extra={
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => {
                  setAddKind('category');
                  addForm.resetFields();
                }}
              >
                新增分类
              </Button>
            }
          >
            <List
              {...listProps(
                'category',
                categories,
                categoriesQuery.isLoading,
                '暂无订单分类',
              )}
            />
            <div style={{ marginTop: 8, color: 'rgba(0,0,0,.45)', fontSize: 12 }}>
              名称含「支出」即按支出方向记账，对方（客户）看到的分类会自动翻成相反方向。
            </div>
          </Card>
        </Col>
      </Row>

      <Modal
        title={addKind ? `新增${TITLES[addKind]}` : ''}
        open={addKind !== null}
        okText="创建"
        cancelText="取消"
        confirmLoading={createItem.isPending}
        onOk={() => void submitAdd()}
        onCancel={() => setAddKind(null)}
        destroyOnHidden
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, whitespace: true, message: '请输入名称' }]}
          >
            <Input placeholder={addKind === 'service' ? '如：直播服务' : '如：支出订单-外采'} maxLength={20} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editTarget ? `编辑${TITLES[editTarget.kind]}` : ''}
        open={editTarget !== null}
        okText="保存"
        cancelText="取消"
        confirmLoading={updateItem.isPending}
        onOk={() => void submitEdit()}
        onCancel={() => setEditTarget(null)}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          {editTarget?.kind === 'service' && (
            // 后台这里刻意不给「N 张历史订单」的具体数字：`GET /service-types` 的 order_count
            // 是按登录人自己的订单数的，管理员改的是系统预设，受影响的是全体用户——
            // 拿本人名下那个数（多半是 0）去说影响面，比不写数字更容易误导。
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              title="改名只影响新建订单"
              description="订单上落的是服务类型名称，改名不会回写历史订单，各用户已有的单子仍显示旧名。"
            />
          )}
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, whitespace: true, message: '请输入名称' }]}
          >
            <Input maxLength={20} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
